<script setup lang="ts">
import type { GlobalSearchResult } from "~/utils/globalsearch"

const props = defineProps<{ item: GlobalSearchResult; search: string }>()

const { t } = useI18n()

function highlightedSiteName(site: { uuid: string; name: string }) {
  if (props.item.type !== "contract" && props.item.type !== "invoice")
    return sanitizeMeiliHighlight(site.name)
  const index = props.item.sites.findIndex((candidate) => candidate.uuid === site.uuid)
  return sanitizeMeiliHighlight(props.item._formatted.sites?.[index]?.name ?? site.name)
}

// Contracts and invoices are identified by their contract purchase order when it exists, falling
// back to their site names otherwise.
const highlightedPurchaseOrder = computed(() => {
  const { item } = props
  const [purchaseOrder, formatted] =
    item.type === "contract"
      ? [item.purchase_order, item._formatted.purchase_order]
      : item.type === "invoice"
        ? [item.contract.purchase_order, item._formatted.contract?.purchase_order]
        : [null, null]

  if (!purchaseOrder?.trim()) return null

  return sanitizeMeiliHighlight(formatted ?? purchaseOrder)
})

const counterpart = computed(() => (props.item.type === "contract" ? props.item.counterpart : null))

const logoLoadFailed = ref(false)
</script>

<template>
  <div class="flex w-full flex-row items-center gap-x-5">
    <div v-tippy="t(`globalsearch.type.${item.type}`)" class="size-5">
      <UIcon
        v-if="item.type !== 'site'"
        :name="$icons.model[item.type] ?? $icons.warning"
        class="mx-1 size-4 opacity-70"
        :class="mapModelIconColor[item.type]"
      />

      <div v-else>
        <TechnoFA v-if="item.machine_type" icon-style="size-5" :technos="item.machine_type" />
        <UIcon v-else name="i-fa6-solid-image" class="size-5" />
      </div>
    </div>

    <!-- per type rendering -->
    <div
      class="grid w-full min-w-0 items-center gap-x-2"
      style="grid-template-columns: minmax(150px, 350px) 1fr"
    >
      <!-- site -->
      <div v-if="item.type === 'site'" class="contents">
        <div class="inline-flex items-center gap-x-2 justify-self-start">
          <p
            v-overflow="{ theme: 'light' }"
            class="max-w-64 truncate"
            v-html="sanitizeMeiliHighlight(item._formatted.name)"
          />
          <BadgeIsExternal :is_external="item.is_external" />
        </div>

        <div class="inline-flex items-center justify-end gap-x-2">
          <BadgeSiteStatus class="max-w-32" :status="item.status" />
          <p class="inline-flex min-w-24 items-center justify-end gap-x-1 text-right">
            <span v-if="R.isNumber(item.capacity)" class="font-medium tabular-nums text-gray-600">
              {{
                $n(
                  item.capacity * getUnitConversionRate("kW", "MW"),
                  { minimumFractionDigits: 3, maximumFractionDigits: 3 } as any,
                  "fr-FR",
                )
              }}
            </span>
            <span class="mt-[0.15rem] text-xs text-gray-400">{{ t(`unit.MW`) }}</span>
          </p>
        </div>
      </div>

      <!-- contract -->
      <div v-else-if="item.type === 'contract'" class="contents">
        <div class="flex min-w-0 items-center gap-x-2">
          <div class="flex min-w-0 flex-1 flex-col items-start">
            <div class="inline-flex min-w-0 max-w-full items-center gap-x-2">
              <p
                v-if="highlightedPurchaseOrder"
                v-overflow="{ theme: 'light' }"
                class="min-w-0 truncate"
                v-html="highlightedPurchaseOrder"
              />
              <LabelSiteList
                v-else
                :sites="item.sites"
                :link="false"
                overflow-theme="light"
                class="min-w-0"
              >
                <template #name="{ site }"><span v-html="highlightedSiteName(site)" /></template>
              </LabelSiteList>
              <BadgeIsExternal
                class="shrink-0"
                :is_external="item.sites.some((site) => site.is_external)"
              />
            </div>

            <LabelSiteList
              v-if="highlightedPurchaseOrder"
              :sites="item.sites"
              :link="false"
              overflow-theme="light"
              class="max-w-full text-xs text-gray-400"
            >
              <template #name="{ site }"><span v-html="highlightedSiteName(site)" /></template>
            </LabelSiteList>
          </div>

          <!-- fixed width: keeps contract types aligned across contract and invoice rows -->
          <span class="w-20 shrink-0 text-center text-sm font-normal leading-tight text-gray-400">
            {{ t(`contract.type.${item.contract_type}`) }}
          </span>
        </div>

        <div class="inline-flex items-center justify-end gap-x-2 gap-y-1">
          <div class="flex w-[7.7rem] shrink-0 justify-center">
            <div class="inline-flex max-w-full flex-col">
              <ContractStatusBadge :status="item.current_status" />
              <PercentageBar
                class="ml-0.5 h-1 w-full px-0.5"
                :progress="toHorizonProgress(item.start, item.end)"
              />
            </div>
          </div>
          <div class="flex min-w-24 shrink-0 justify-end pl-4">
            <img
              v-if="counterpart?.logo && !logoLoadFailed"
              v-tippy="counterpart?.name"
              class="mx-auto h-8 w-10 max-w-min truncate object-contain text-xs text-gray-400"
              :alt="counterpart?.name"
              :src="counterpart?.logo"
              @error="logoLoadFailed = true"
            />
            <UIcon
              v-else
              v-tippy="counterpart?.name"
              name="i-fa6-solid-image"
              class="mx-auto size-4 text-gray-400"
            />
          </div>
        </div>
      </div>

      <!-- invoice -->
      <div v-else-if="item.type === 'invoice'" class="contents">
        <div class="flex min-w-0 items-center gap-x-2">
          <div class="flex min-w-0 flex-1 flex-col items-start">
            <div class="inline-flex min-w-0 max-w-full items-center gap-x-2">
              <p
                v-if="highlightedPurchaseOrder"
                v-overflow="{ theme: 'light' }"
                class="min-w-0 truncate"
                v-html="highlightedPurchaseOrder"
              />
              <LabelSiteList
                v-else
                :sites="item.sites"
                :link="false"
                overflow-theme="light"
                class="min-w-0"
              >
                <template #name="{ site }"><span v-html="highlightedSiteName(site)" /></template>
              </LabelSiteList>
              <BadgeIsExternal
                class="shrink-0"
                :is_external="item.sites.some((site) => site.is_external)"
              />
            </div>

            <div class="-ml-1 inline-flex min-w-0 max-w-full items-center gap-x-2">
              <HorizonSpan class="shrink-0" :start="item.start" :end="item.end" :badge="true" />
              <UIcon
                v-if="item.is_birthday"
                :name="$icons.birthday"
                class="h-4 shrink-0 text-yellow-600"
              />
            </div>
          </div>

          <!-- fixed width: keeps contract types aligned across contract and invoice rows -->
          <span class="w-20 shrink-0 text-center text-sm font-normal leading-tight text-gray-400">
            {{ t(`contract.type.${item.contract.type}`) }}
          </span>
        </div>

        <div class="inline-flex items-center justify-end gap-x-2">
          <div class="flex w-[7.7rem] shrink-0 justify-center">
            <BareBadgeInvoiceStatus
              :status="item.status"
              :is_only_credit_note="item.is_only_credit_note"
              :is_accounted="item.is_accounted"
            />
          </div>
          <p
            v-if="R.isNumber(item.total_line?.amount)"
            class="inline-flex min-w-24 items-center gap-x-2 justify-self-end text-right"
          >
            <span
              v-if="R.isNumber(item.total_line.amount)"
              class="w-full text-right font-medium tabular-nums text-gray-600"
            >
              {{
                $n(
                  item.total_line.amount / 1000,
                  { minimumFractionDigits: 2, maximumFractionDigits: 2 } as any,
                  "fr-FR",
                )
              }}
            </span>
            <span class="text-right text-gray-400">{{ t(`unit.keur`) }}</span>
          </p>
          <div v-else class="min-w-24" />
        </div>
      </div>

      <!-- company -->
      <div
        v-else-if="R.isIncludedIn(item.type, ['assetowner', 'holding', 'portfolio'])"
        class="contents"
      >
        <div class="inline-flex items-center gap-x-2 justify-self-start">
          <p class="max-w-60 truncate" v-html="sanitizeMeiliHighlight(item._formatted.name)" />
        </div>

        <div class="inline-flex items-center justify-end gap-x-2">
          <div
            v-if="R.isNonNullish(item.n_sites)"
            class="inline-flex items-center justify-end gap-x-2"
          >
            <Badge> {{ item.n_sites }} {{ t("sites", item.n_sites) }} </Badge>
          </div>
          <p class="inline-flex min-w-24 items-center justify-end gap-x-1 text-right">
            <span v-if="R.isNumber(item.capacity)" class="font-medium tabular-nums text-gray-600">
              {{
                $n(
                  item.capacity * getUnitConversionRate("kW", "MW"),
                  { minimumFractionDigits: 3, maximumFractionDigits: 3 } as any,
                  "fr-FR",
                )
              }}
            </span>
            <span class="mt-[0.15rem] text-xs text-gray-400">{{ t(`unit.MW`) }}</span>
          </p>
        </div>
      </div>
    </div>

    <GlobalSearchMatchBadgeCloud class="w-24" :item :search />
  </div>
</template>

<style module lang="postcss">
em {
  @apply font-medium not-italic text-rnxblue-800;
}
</style>

<i18n lang="yaml">
en:
  sites: site | sites
fr-FR:
  sites: site | sites
</i18n>
